import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { apiCallBegan, ApiCallFailedPayload } from "../api";
import { authHeader, getWorkerId } from "../users/auth";
import i18n from "../../i18n";
import { AppDispatch, AppState } from "../configureStore";
import { TaskDto } from "entities/processCycle/TaskDto";
import { CreateTaskDto } from "entities/processCycle/CreateTaskDto";

// ---------------- STATE ----------------
interface State {
    list: TaskDto[];
    loading: boolean;
    error: ApiCallFailedPayload | null;
    lastFetch: number | null;
    reloadTrigger: number;
}

const initialState: State = {
    list: [],
    loading: false,
    error: null,
    lastFetch: null,
    reloadTrigger: 0,
};

// ---------------- SLICE ----------------
const slice = createSlice({
    name: "candidateTasks",
    initialState,
    reducers: {
        tasksRequested: (state) => {
            state.loading = true;
            state.error = null;
        },
        tasksReceived: (state, action: PayloadAction<TaskDto[]>) => {
            state.list = action.payload;
            state.loading = false;
            state.lastFetch = Date.now();
        },
        tasksRequestFailed: (state, action: PayloadAction<ApiCallFailedPayload>) => {
            state.loading = false;
            state.error = action.payload;
        },

        taskCreateStarted: (state) => {
            state.loading = true;
            state.error = null;
        },
        taskCreateSuccess: (state, action: PayloadAction<TaskDto>) => {
            state.loading = false;
            state.reloadTrigger += 1;
        },
        taskCreateFailed: (state, action: PayloadAction<ApiCallFailedPayload>) => {
            state.loading = false;
            state.error = action.payload;
        },

        taskActionStarted: (state) => {
            state.loading = true;
            state.error = null;
        },
        taskActionSuccess: (state, action: PayloadAction<TaskDto>) => {
            state.loading = false;
            state.reloadTrigger += 1;
        },
        taskActionFailed: (state, action: PayloadAction<ApiCallFailedPayload>) => {
            state.loading = false;
            state.error = action.payload;
        },

        taskDeleteStarted: (state) => {
            state.loading = true;
            state.error = null;
        },
        taskDeleteSuccess: (state, action: PayloadAction) => {
            state.loading = false;
            state.reloadTrigger += 1;
        },
        taskDeleteFailed: (state, action: PayloadAction<ApiCallFailedPayload>) => {
            state.loading = false;
            state.error = action.payload;
        }


    },
});

export const {
    tasksRequested,
    tasksReceived,
    tasksRequestFailed,

    taskCreateStarted,
    taskCreateSuccess,
    taskCreateFailed,

    taskActionStarted,
    taskActionSuccess,
    taskActionFailed,

    taskDeleteStarted,
    taskDeleteSuccess,
    taskDeleteFailed
} = slice.actions;

export default slice.reducer;

// ---------------- API CALLS ----------------

// List tasks for worker

export const getActiveTasksForWorker = (workerId?: number) => (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.candidateTasks.loading) return;

    const id = workerId ?? getWorkerId(getState());
    const url = `/api/CandidateProcess/worker/${id}/tasks`;

    dispatch(apiCallBegan({
        url,
        method: "GET",
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onStart: tasksRequested.type,
        onSuccess: tasksReceived.type,
        onError: tasksRequestFailed.type
    }));
};

// Create task 
export const createTask = (workerId: number) => (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.candidateTasks.loading) return;

    const url = `/api/CandidateProcess/create/${workerId}`;

    dispatch(apiCallBegan({
        url,
        method: "POST",
        headers: {
            ...authHeader(),
            "Accept-Language": i18n.language,
            "Content-Type": "application/json"
        },
        onStart: taskCreateStarted.type,
        onSuccess: taskCreateSuccess.type,
        onError: taskCreateFailed.type
    }));
};

// Delete process
export const deleteTask = (processId: number) => (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.candidateTasks.loading) return;

    const url = `/api/CandidateProcess/${processId}`;
    dispatch(apiCallBegan({
        url,
        method: "DELETE",
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onStart: taskDeleteStarted.type,
        onSuccess: taskDeleteSuccess.type,
        onError: taskDeleteFailed.type
    }));
};

// Mark job offer as approved
export const approveJobOffer = (taskId: number) => (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.candidateTasks.loading) return;

    let url = `/api/CandidateProcess/task/${taskId}/joboffer/approve`;

    dispatch(apiCallBegan({
        url,
        method: "POST",
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onStart: taskActionStarted.type,
        onSuccess: taskActionSuccess.type,
        onError: taskActionFailed.type
    }));
};

// reject task
export const rejectTask = (taskId: number, comment: string) => (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.candidateTasks.loading) return;

    let url = `/api/CandidateProcess/task/${taskId}/reject`;

    dispatch(apiCallBegan({
        url,
        method: "POST",
        data: { SenderComment: comment },
        headers: {
            ...authHeader(),
            "Accept-Language": i18n.language,
            "Content-Type": "application/json"
        },
        onStart: taskActionStarted.type,
        onSuccess: taskActionSuccess.type,
        onError: taskActionFailed.type
    }));
};

//complete task
export const confirmTasksDataByWorkerId = (workerId?: number) => (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.candidateTasks.loading) return;

    const id = workerId ?? getWorkerId(getState());

    let url = `/api/CandidateProcess/worker/${id}/tasks/confirmData`;

    dispatch(apiCallBegan({
        url,
        method: "POST",
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onStart: taskActionStarted.type,
        onSuccess: taskActionSuccess.type,
        onError: taskActionFailed.type
    }));
};

